import { put, takeEvery, call, select } from "redux-saga/effects";
import { redirectToLoginPage } from "../utils/auth-provider";
import { RootState } from "../redux/reducers/rootReducer";
import { CLEAR_USER_INFO } from "../redux/types/userTypes";

// Use a flag to prevent multiple redirects when multiple 401s come in simultaneously
let isHandling401 = false;

// Watcher: listen to specific action and call worker
export function* watchHttpAction() {
  yield takeEvery("*", sendHttpRequest);
}

// Worker: send HTTP request
export function* sendHttpRequest(action: any): Generator<any, void, any> {
  if ("http" in action && "cognito" in action.http) {
    yield call(cognitoRequestSender, action);
  }
}

// Selectors to get user data from Redux - using direct access fields
const getUserData = (state: RootState) => ({
  token: state.user.token,
  userName: state.user.userName,
  email: state.user.email,
});

// Helper: Add common params (username, email) to request
function addCommonParams(effect: any, userName: string, email: string) {
  let url = effect.url;
  let jsonParams = effect.json || {};

  // Add username and email to query params for non-OPTIONS requests
  if (effect.method !== 'OPTIONS') {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}UserName=${userName}`;
    if (email) {
      url = `${url}&Email=${email}`;
    }
  }

  // Add username to JSON body for non-GET requests
  if (effect.method !== 'GET' && !Array.isArray(jsonParams)) {
    jsonParams = {
      ...jsonParams,
      UserName: userName,
    };
  }

  return { url, jsonParams };
}

/**
 * Handle 401 Unauthorized response:
 * Clear user info and redirect to login page for re-authentication.
 * 
 * Note: We skip silent token refresh and go directly to redirect because:
 * - Auth.currentSession() may return a cached token that the API still rejects
 * - This ensures the user always gets a fresh token from the identity provider
 */
function* handleTokenExpired(): Generator<any, void, any> {
  // Prevent multiple concurrent 401 handling (e.g., multiple API calls failing at once)
  if (isHandling401) {
    return;
  }
  isHandling401 = true;

  try {
    console.warn("[Saga] ⚠️ 401 Unauthorized - clearing session and redirecting to login page...");

    // Clear user info from Redux
    yield put({ type: CLEAR_USER_INFO });

    // Redirect to login page for re-authentication
    // This triggers a full OAuth flow with the identity provider (LASSO/AmazonFederate)
    redirectToLoginPage();
  } finally {
    // Reset the flag after a short delay to allow for page navigation
    setTimeout(() => { isHandling401 = false; }, 5000);
  }
}

// Send HTTP request with Cognito authorization
function* cognitoRequestSender(action: any): Generator<any, void, any> {
  const { effect, commit, rollback } = action.http.cognito;
  
  // Get token, username, and email from Redux store
  const { token, userName, email }: { token?: string; userName?: string; email?: string } = yield select(getUserData);

  // Verify if the session is still valid
  if (token && token !== "") {
    // Add common params (username, email) to request
    const { url, jsonParams } = addCommonParams(effect, userName || '', email || '');

    try {
      const response: Response = yield call(fetch, url, {
        method: effect.method,
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
          'X-Requested-With': 'XMLHttpRequest', // CSRF protection
          'X-MSA-Client': 'web-app', // Custom client identifier
          'X-MSA-Client-Version': '1.0.0', // Client version for tracking
          ...(effect.headers || {}),
        },
        body: effect.method !== 'GET' ? JSON.stringify(jsonParams) : null,
      });

      if (response.ok) {
        // Success
        const jsonResponse: string = yield call([response, 'text']);
        yield put({
          ...commit,
          statusCode: response.status,
          payload: JSON.parse(jsonResponse.replace(/\bNaN\b/g, "null")),
        });
      } else if (response.status === 401) {
        // 401 Unauthorized - Token expired
        // Directly redirect to login page for re-authentication
        console.warn("[Saga] ⚠️ Received 401 Unauthorized for action:", action.type);
        yield call(handleTokenExpired);
      } else {
        // Other failure (non-401)
        const jsonResponse: string = yield call([response, 'text']);
        yield put({
          ...rollback,
          statusCode: response.status,
          payload: (() => { try { return JSON.parse(jsonResponse); } catch { return { message: jsonResponse }; } })(),
        });
      }
    } catch (e: any) {
      console.error("[Saga] HTTP request error:", e);
      // Network errors (including CORS errors) may indicate the API Gateway returned
      // a 401 without proper CORS headers. In this case, the browser blocks the response
      // and we get a TypeError/NetworkError instead of the actual 401 status.
      // Check if this might be an auth issue by examining the error.
      const errorMessage = e?.message?.toLowerCase() || '';
      if (
        errorMessage.includes('failed to fetch') ||
        errorMessage.includes('network') ||
        errorMessage.includes('cors')
      ) {
        console.warn("[Saga] ⚠️ Network/CORS error - may be caused by expired token (API Gateway 401 without CORS headers)");
        console.warn("[Saga] Treating as potential auth failure - redirecting to login...");
        yield call(handleTokenExpired);
      } else {
        yield put(rollback);
      }
    }
  } else {
    // Token not yet available in Redux - this means auth hasn't completed yet.
    // Don't redirect to login! Just dispatch rollback so the component can handle it.
    // The App.tsx auth gate ensures components only render after auth completes,
    // so this branch should rarely be hit.
    console.warn("[Saga] No token in Redux store for action:", action.type, "- auth may still be initializing");
    yield put({
      ...rollback,
      statusCode: 0,
      payload: { message: "Authentication not yet complete" },
    });
  }
}
